import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daio1sboh653ivfvoh8g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <form
      id="chigua-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="nickname" className="block font-label text-xs font-medium text-foreground-700">
            怎么称呼你
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            maxLength={40}
            placeholder="例如：一颗西瓜籽"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="block font-label text-xs font-medium text-foreground-700">
            你的邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={80}
            placeholder="you@example.com"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="topic" className="block font-label text-xs font-medium text-foreground-700">
          想聊哪一类
        </label>
        <select
          id="topic"
          name="topic"
          defaultValue="线索投稿"
          className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="线索投稿">线索投稿</option>
          <option value="内容建议">内容建议</option>
          <option value="纠错反馈">纠错反馈</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="block font-label text-xs font-medium text-foreground-700">
          想说的话
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="请尽量附上公开来源，方便我们核对后补进时间线。"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 font-body text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <input
        className="hp-field"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-send-plane-line" />
          </span>
          {status === 'submitting' ? '提交中…' : '提交给我们'}
        </button>
        <p className="font-body text-xs text-foreground-500">
          我们只在核对线索时使用你的邮箱，不做外发。
        </p>
      </div>

      {status === 'success' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 font-body text-sm text-secondary-900">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </span>
          已收到，谢谢你的补课线索，我们会尽快核对。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 font-body text-sm text-primary-800">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {formError || '提交失败，请稍后再试。'}
        </p>
      )}
    </form>
  );
}